//dashboard/src/pages/OperationsAssociate/AssetsReturnManagementPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Eye, CheckCircle, XCircle, Filter as FilterIcon, 
  ArrowLeftCircle, Clock, Activity, AlertCircle,
  RotateCcw, ShieldCheck, ClipboardCheck, Archive,
  Search, Info, Loader2
} from "lucide-react";
import moment from "moment";

import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import AssetsTabs from "../../components/operationsAssociate/AssetsTabs";
import ReturnRequestReviewModal from "../../components/operationsAssociate/ReturnRequestReviewModal";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";

const API_URL = "https://ims.holdthechild.net";

const AssetReturnRequestsPage = () => {
    // --- UI State ---
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Data State ---
    const [requests, setRequests] = useState([]);
    const [filterStatus, setFilterStatus] = useState("Pending");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    const fetchReturnRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/api/return-requests`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { status: filterStatus },
            });
            setRequests(response.data.data || []);
        } catch (err) {
            setError("Registry connection intermittent.");
            toast.error("Failed to sync return requests ❌");
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { fetchReturnRequests(); }, [fetchReturnRequests]);

    const handleOpenReviewModal = (request) => {
        setSelectedRequest(request);
        setIsReviewModalOpen(true);
    };

    const handleCloseReviewModal = () => {
        setIsReviewModalOpen(false);
        setSelectedRequest(null);
        fetchReturnRequests();
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-10">
                        
                        {/* Tab Navigation Hub */}
                        <div className="flex flex-col items-center">
                            <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-gray-100 inline-block mb-8">
                                <AssetsTabs />
                            </div>
                            
                            <PremiumHeader 
                                title="Return" 
                                lastWordAccent="Protocols" 
                                subtitle="Centralized monitoring of equipment reintegration requests and asset custody handover verification." 
                            />
                        </div>

                        {/* Action & Filter Bar */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex items-center gap-4 bg-white p-2 rounded-2xl border border-gray-100 shadow-sm w-full md:w-auto">
                                <div className="p-3 bg-gray-50 rounded-xl">
                                    <FilterIcon className="text-gray-400" size={16} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest pl-1">Filtration Buffer</span>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="bg-transparent border-none text-[11px] font-black uppercase tracking-widest focus:ring-0 cursor-pointer text-gray-800"
                                    >
                                        <option value="Pending">Pending Review</option>
                                        <option value="Accepted">Verified Returns</option>
                                        <option value="Rejected">Flagged / Rejected</option>
                                        <option value="">Full Registry</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 bg-emerald-50/50 p-2 rounded-2xl">
                                <div className="p-2 bg-white rounded-xl shadow-sm">
                                    <ShieldCheck className="text-emerald-600" size={14} />
                                </div>
                                <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest mr-2">Secured Registry Protocol</span>
                            </div>
                        </div>

                        {/* Status Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <PremiumStatCard 
                                title="Pending Review" 
                                value={requests.filter(r => r.status === 'Pending').length} 
                                icon={<Clock className="text-orange-600" />} 
                                iconBg="bg-orange-50"
                                description="Awaiting processing"
                            />
                            <PremiumStatCard 
                                title="Verification" 
                                value={requests.filter(r => r.status === 'Accepted').length} 
                                icon={<ClipboardCheck className="text-emerald-600" />} 
                                iconBg="bg-emerald-50"
                                description="Successfully returned"
                            />
                            <PremiumStatCard 
                                title="Flagged Actions" 
                                value={requests.filter(r => r.status === 'Rejected').length} 
                                icon={<AlertCircle className="text-red-600" />} 
                                iconBg="bg-red-50"
                                description="Review required"
                            />
                            <PremiumStatCard 
                                title="Total Volume" 
                                value={requests.length} 
                                icon={<Activity className="text-blue-600" />} 
                                iconBg="bg-blue-50"
                                description="Life-cycle operations"
                            />
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                                <Loader2 className="animate-spin text-orange-600 mb-6" size={48} />
                                <p className="text-[11px] font-black uppercase tracking-[0.4em] text-gray-400">Accessing Return Ledger Logs...</p>
                            </div>
                        ) : error ? (
                            <div className="p-16 text-center bg-red-50 rounded-[3rem] border border-red-100">
                                <div className="p-6 bg-red-100/50 rounded-full w-fit mx-auto mb-6">
                                    <Info className="text-red-600" size={32} />
                                </div>
                                <h3 className="text-xl font-black text-red-900 uppercase tracking-tight mb-2">Registry Link Failed</h3>
                                <p className="text-red-600/70 font-medium mb-8 max-w-md mx-auto">{error}</p>
                                <button onClick={() => fetchReturnRequests()} className="bg-red-600 text-white px-10 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest flex items-center gap-3 mx-auto hover:bg-red-700 shadow-xl shadow-red-100">
                                    <RotateCcw size={16} /> Re-Initialize Sync
                                </button>
                            </div>
                        ) : (
                            <PremiumCard icon={<Archive size={18} />} title="Reintegration Buffer">
                                <div className="p-2 overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="bg-gray-50/50">
                                                <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Asset Identity</th>
                                                <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Requester / Context</th>
                                                <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Reasoning Buffer</th>
                                                <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">Temporal Log</th>
                                                <th className="px-8 py-6 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">Operational Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100/50">
                                            {requests.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="px-8 py-24 text-center">
                                                        <div className="flex flex-col items-center opacity-40">
                                                            <Archive size={40} className="mb-4 text-gray-300" />
                                                            <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Zero Return Requests in Current Buffer</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                requests.map((request, idx) => (
                                                    <motion.tr 
                                                        key={request._id}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                        className="group hover:bg-gray-50/50 transition-colors"
                                                    >
                                                        <td className="px-8 py-6">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-black text-gray-800 uppercase leading-none tracking-tight">{request.asset?.name || "N/A Node"}</span>
                                                                <span className="text-[10px] font-black text-orange-600 mt-1.5 uppercase tracking-widest flex items-center gap-1.5">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-orange-600 animate-pulse" />
                                                                    {request.asset?.serialNumber || "SN-EMPTY"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <div className="flex items-center gap-3">
                                                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm font-black text-[10px] text-blue-600">
                                                                    {request.requestedBy?.firstName?.[0]}{request.requestedBy?.lastName?.[0]}
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[11px] font-black text-gray-700 uppercase tracking-tight">
                                                                        {request.requestedBy ? `${request.requestedBy.firstName} ${request.requestedBy.lastName}` : "GUEST USER"}
                                                                    </span>
                                                                    <span className="text-[9px] font-bold text-gray-400 uppercase">Operational Staff</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <div className="bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 max-w-[240px] group-hover:bg-white transition-colors">
                                                                <p className="text-[10px] font-bold text-gray-500 uppercase line-clamp-2 leading-relaxed italic">"{request.reason}"</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <div className="flex flex-col">
                                                                <span className="text-[11px] font-black text-gray-700 uppercase">{moment(request.requestDate).format("DD MMM YYYY")}</span>
                                                                <div className="flex items-center gap-1 text-[9px] font-bold text-gray-400 mt-1">
                                                                    <Clock size={10} />
                                                                    {moment(request.requestDate).format("HH:mm")}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6 text-right">
                                                            <button 
                                                                onClick={() => handleOpenReviewModal(request)}
                                                                className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-2xl hover:bg-orange-600 transition-all font-black text-[10px] uppercase tracking-widest transform hover:scale-105 active:scale-95 shadow-lg shadow-gray-200"
                                                            >
                                                                <Eye size={14} />
                                                                Verify Request
                                                            </button>
                                                        </td>
                                                    </motion.tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </PremiumCard>
                        )}
                    </div>
                </main>
            </div>

            {/* --- Modals --- */}
            <AnimatePresence>
                {isReviewModalOpen && selectedRequest && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }}
                            onClick={handleCloseReviewModal}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative w-full max-w-4xl bg-white rounded-[3rem] overflow-hidden shadow-2xl border border-gray-100"
                        >
                            <ReturnRequestReviewModal
                                request={selectedRequest}
                                closeModal={handleCloseReviewModal}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AssetReturnRequestsPage;